import { useEffect, useState, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import { useSelector, useDispatch } from "react-redux";
import {
  globalStyle,
  handleDangerVisible,
  handleVisible,
} from "../../global/Utils";
import {
  getBuyer,
  deleteBuyer,
  refreshBuyerStatus,
} from "../../Actions/BuyerAction";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton, Tooltip, Box, useTheme, useMediaQuery } from "@mui/material";
import Swal from "sweetalert2";
import { isUndefined, debounce } from "lodash";
import { useNavigate } from "react-router-dom";
import MobileViewTabelList from "../Common/MobileViewTabelList";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SyncIcon from "@mui/icons-material/Sync";
import { TaxStatusChip } from "./TaxStatusBox";

const BuyersList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const [globalFilter, setGlobalFilter] = useState("");

  const isGettingBuyer = useSelector(
    (state) => state.BuyerReducer?.isGettingBuyer,
  );
  const buyerList = useSelector((state) => state.BuyerReducer?.buyerList || []);
  const totalResults = useSelector(
    (state) => state.BuyerReducer?.totalResults || 0,
  );

  useEffect(() => {
    dispatch(getBuyer(pagination.pageIndex, pagination.pageSize, globalFilter));
  }, [pagination, dispatch, globalFilter]);

  const debouncedSetFilter = useMemo(
    () =>
      debounce((value) => {
        setGlobalFilter(value);
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetFilter.cancel();
    };
  }, [debouncedSetFilter]);

  const refreshingBuyerStatusId = useSelector(
    (state) => state.BuyerReducer?.refreshingBuyerStatusId,
  );

  // Re-checks the buyer's Sales Tax and ATL status and saves what they are now.
  const handleRefreshStatus = (buyer) => {
    dispatch(
      refreshBuyerStatus(
        buyer._id,
        handleVisible,
        handleDangerVisible,
        pagination.pageIndex,
        pagination.pageSize,
        globalFilter,
      ),
    );
  };

  // The button for it: off for a buyer with no NTN / CNIC to check, and while
  // that buyer's check is running.
  const renderRefreshStatus = (buyer) => {
    const hasNumber = Boolean(String(buyer?.registrationNo || "").trim());
    const isRefreshing = refreshingBuyerStatusId === buyer?._id;
    return (
      <Tooltip
        title={
          !hasNumber
            ? "No NTN / CNIC to check"
            : isRefreshing
              ? "Checking..."
              : "Refresh tax status"
        }
      >
        <span>
          <IconButton
            size="small"
            aria-label="Refresh tax status"
            disabled={!hasNumber || isRefreshing}
            onClick={() => handleRefreshStatus(buyer)}
            sx={{ color: "black" }}
          >
            <SyncIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const handleDeleteBuyer = (row) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this record?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#6c757d",
      customClass: {
        popup: "mui-swal-popup",
      },
      didOpen: () => {
        const container = document.querySelector(".swal2-container");
        if (container) {
          container.style.zIndex = "2000"; // higher than MUI
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(
          deleteBuyer(
            row.original._id,
            handleVisible,
            handleDangerVisible,
            pagination.pageIndex,
            pagination.pageSize,
            globalFilter,
          ),
        );
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "_id",
        header: "#",
        size: 40,
        enableResizing: false,
        enableEditing: false,
        Cell: ({ row }) =>
          row.index + 1 + pagination.pageIndex * pagination.pageSize,
      },
      {
        accessorKey: "businessName",
        header: "Bussiness Name",
        size: 150, // slightly wider
        grow: 1, // fixed width
        enableEditing: false,
      },
      {
        accessorKey: "registrationNo",
        header: "NTN/Registration No",
        size: 115,
        grow: 1, // take flexible space
        enableEditing: false,
      },
      {
        accessorKey: "registrationType",
        header: "Registration Type",
        size: 115,
        grow: 1, // take flexible space
        enableEditing: false,
      },
      // Saved when the buyer was added (FBR sales tax / Active Taxpayer List).
      {
        accessorKey: "salesTaxStatus",
        header: "ST Status",
        size: 110,
        grow: 1,
        enableEditing: false,
        Cell: ({ row }) => (
          <TaxStatusChip status={row.original.salesTaxStatus} />
        ),
      },
      {
        accessorKey: "atlStatus",
        header: "ATL Status",
        size: 110,
        grow: 1,
        enableEditing: false,
        Cell: ({ row }) => <TaxStatusChip status={row.original.atlStatus} />,
      },
      {
        accessorKey: "contact",
        header: "Contact Number",
        size: 120,
        grow: 1,
        enableEditing: false,
      },
      {
        accessorKey: "province",
        header: "Province",
        size: 123,
        grow: 1,
        enableEditing: false,
      },
      {
        accessorKey: "createdBy.email",
        header: "Created By",
        grow: true,
        enableEditing: false,
      },
      {
        id: "actions",
        header: "Actions",
        size: 165,
        grow: 0, // fixed width
        enableEditing: false,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Box display="flex" gap={1}>
            <Tooltip title="View">
              <IconButton
                size="small"
                onClick={() =>
                  navigate(`/main/view-buyer-details?id=${row?.original?._id}`)
                }
                sx={{
                  color: "black",
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Ledger">
              <IconButton
                size="small"
                onClick={() =>
                  navigate(`/main/buyer-ledger?id=${row?.original?._id}`)
                }
                sx={{ color: "black" }}
              >
                <ReceiptLongIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {renderRefreshStatus(row.original)}
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() =>
                  navigate("/main/edit-buyer", { state: row.original })
                }
                sx={{ color: "black" }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => handleDeleteBuyer(row)}
                sx={{ color: "black" }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    // eslint-disable-next-line
    [pagination, refreshingBuyerStatusId],
  );

  const renderMobileBuyerCards = () => {
    return buyerList.map((item, index) => {
      const serial = index + 1 + pagination.pageIndex * pagination.pageSize;

      return (
        <Box
          key={item._id}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            backgroundColor: "#fff",
            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          }}
        >
          {/* Top Row: Serial + Actions */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Box sx={{ fontSize: 12, color: "#999" }}>#{serial}</Box>

            <Box display="flex" gap={1}>
              <Tooltip title="View">
                <IconButton
                  size="small"
                  onClick={() =>
                    navigate(`/main/view-buyer-details?id=${item?._id}`)
                  }
                  sx={{
                    color: "black",
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Ledger">
                <IconButton
                  size="small"
                  onClick={() =>
                    navigate(`/main/buyer-ledger?id=${item?._id}`)
                  }
                  sx={{ color: "black" }}
                >
                  <ReceiptLongIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {renderRefreshStatus(item)}
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() => navigate("/main/edit-buyer", { state: item })}
                  sx={{ color: "black" }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteBuyer({ original: item })}
                  sx={{ color: "black" }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Business Name */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>
              Business Name:
            </b>
            <Box sx={{ fontSize: 14 }}>{item.businessName || "-"}</Box>
          </Box>

          {/* Registration No */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>
              NTN / Registration:
            </b>
            <Box sx={{ fontSize: 14 }}>{item.registrationNo || "-"}</Box>
          </Box>

          {/* Registration Type */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>
              Registration Type:
            </b>
            <Box sx={{ fontSize: 14 }}>{item.registrationType || "-"}</Box>
          </Box>

          {/* ST / ATL Status */}
          <Box sx={{ mb: 1, display: "flex", gap: 3 }}>
            <Box>
              <b className={`text-[${globalStyle.bgColorBlue}]`}>ST Status:</b>
              <Box sx={{ fontSize: 14 }}>
                <TaxStatusChip status={item.salesTaxStatus} />
              </Box>
            </Box>
            <Box>
              <b className={`text-[${globalStyle.bgColorBlue}]`}>ATL Status:</b>
              <Box sx={{ fontSize: 14 }}>
                <TaxStatusChip status={item.atlStatus} />
              </Box>
            </Box>
          </Box>

          {/* Contact */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Contact:</b>
            <Box sx={{ fontSize: 14 }}>{item.contact || "-"}</Box>
          </Box>

          {/* Province */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Province:</b>
            <Box sx={{ fontSize: 14 }}>{item.province || "-"}</Box>
          </Box>

          {/* Created By */}
          <Box>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Created By:</b>
            <Box sx={{ fontSize: 14 }}>{item?.createdBy?.email || "-"}</Box>
          </Box>
        </Box>
      );
    });
  };

  return (
    <Box className="p-4">
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap", // important for mobile
          gap: 1,
        }}
      >
        <Box>
          <Box
            component="h1"
            sx={{
              fontFamily: globalStyle.fontFamilyHeading,
              fontWeight: 700,
              color: globalStyle.bgColorBlue,
              fontSize: {
                xs: globalStyle.headingFontSizeSmall, // mobile
                sm: globalStyle.headingFontSize, // tablet+
              },
              lineHeight: 1.2,
            }}
          >
            Buyer Information
          </Box>

          {/* Optional subtitle (mobile friendly) */}
          <Box
            component="p"
            sx={{
              mt: 0.5,
              fontFamily: globalStyle.fontFamilyParagraph,
              fontSize: {
                xs: 12,
                sm: 14,
              },
              color: "#666",
            }}
          >
            Manage and view all Buyers records
          </Box>
        </Box>
      </Box>
      {isMobile ? (
        <MobileViewTabelList
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          pagination={pagination}
          setPagination={setPagination}
          totalResults={totalResults}
          loading={isGettingBuyer}
          searchLabel="Buyers"
        >
          {renderMobileBuyerCards()}
        </MobileViewTabelList>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={buyerList}
          enableRowActions={false}
          manualPagination
          rowCount={totalResults}
          state={{
            columnOrder: [
              "_id",
              "businessName",
              "registrationNo",
              "registrationType",
              "salesTaxStatus",
              "atlStatus",
              "contact",
              "province",
              "createdBy.email",
              "actions",
            ],
            columnPinning: {
              right: ["actions"],
            },
            isLoading: isGettingBuyer,
            pagination,
          }}
          onPaginationChange={setPagination}
          onGlobalFilterChange={(value) =>
            isUndefined(value)
              ? debouncedSetFilter("")
              : debouncedSetFilter(value)
          }
          columnFilterDisplayMode={false}
          muiTablePaperProps={{
            sx: {
              backgroundColor: globalStyle.bgColorWhite,
              borderRadius: `${globalStyle.borderRadius}px`,
              fontFamily: globalStyle.fontFamily,
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: globalStyle.blueColor,
              color: "#fff",
              fontSize: globalStyle.fontSize,
              fontFamily: globalStyle.fontFamilyHeading,
              fontWeight: 600,
              borderBottom: "none",
              whiteSpace: "normal",
              wordBreak: "break-word",
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              fontSize: globalStyle.fontSize,
              fontFamily: globalStyle.fontFamilyParagraph,
              color: "#333",
              borderBottom: `1px solid ${globalStyle.curveLightColor}`,
              whiteSpace: "normal",
              wordBreak: "break-word",
            },
          }}
          muiTableBodyRowProps={{
            sx: {
              backgroundColor: globalStyle.bgColorWhite,
              "&:hover": {
                backgroundColor: globalStyle.bgColor,
              },
            },
          }}
          muiTableContainerProps={{
            sx: {
              maxHeight: "none", // ✅ remove height restriction
              overflow: "visible", // ✅ allow content to expand
              borderRadius: `${globalStyle.borderRadius}px`,
            },
          }}
          muiPaginationProps={{
            sx: {
              "& .MuiTablePagination-toolbar": {
                fontFamily: globalStyle.fontFamily,
              },
              "& .Mui-selected": {
                backgroundColor: globalStyle.blueColor,
                color: "#fff",
              },
            },
          }}
          muiTableProps={{
            sx: {
              tableLayout: "fixed",
            },
          }}
        />
      )}
    </Box>
  );
};

export default BuyersList;
